import styled, { keyframes, css } from "styled-components";
import theme from "../../theme";

// ─── Theme shortcuts ────────────────────────────────────────────────────────
const { primary, bgColor, border } = theme.colors;
const cardBg = theme.colors.background.paper;
const textPrimary = theme.colors.text.primary;
const textSecondary = theme.colors.text.secondary;
const textTertiary = theme.colors.text.tertiary;
const gradient = theme.colors.gradient.themeGradient;

// ─── Keyframe animations ────────────────────────────────────────────────────
const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(64, 224, 208, 0.4); }
  50%       { box-shadow: 0 0 0 12px rgba(64, 224, 208, 0);  }
`;

const typing = keyframes`
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.4; }
  40%            { transform: scale(1);   opacity: 1;   }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

// Floating trigger button
const FAB = styled.button`
  position: fixed;
  bottom: 20px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${gradient};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  box-shadow: 0 4px 20px rgba(64, 224, 208, 0.35);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  animation: ${pulse} 2.5s infinite;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 28px rgba(64, 224, 208, 0.5);
  }

  svg {
    font-size: 26px;
  }
`;

// Chat window wrapper — handles both normal popup & full-screen
const ChatWindow = styled.div`
  position: fixed;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  background: ${bgColor};
  border: 1px solid ${border};
  overflow: hidden;
  animation: ${fadeSlideUp} 0.3s ease forwards;

  ${({ $fullscreen }) =>
    $fullscreen
      ? css`
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          border-radius: 0;
          border: none;
        `
      : css`
          bottom: 90px;
          right: 24px;
          width: 400px;
          height: 520px;
          border-radius: 16px;

          @media (max-width: 480px) {
            width: calc(100vw - 32px);
            right: 16px;
            bottom: 90px;
            height: 70vh;
          }
        `}
`;

// Header bar
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: ${cardBg};
  border-bottom: 1px solid ${border};
  flex-shrink: 0;
`;

const AvatarWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 20px;
    color: #fff;
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${textPrimary};
  font-family: "Inter", sans-serif;
`;

const OnlineDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a34a;
  margin-right: 5px;
  vertical-align: middle;
`;

const HeaderSub = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  color: ${textTertiary};
  font-family: "Inter", sans-serif;
`;

const IconBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${textTertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 6px;
  transition:
    color 0.2s,
    background 0.2s;

  &:hover {
    color: ${primary};
    background: rgba(64, 224, 208, 0.08);
  }

  svg {
    font-size: 20px;
  }
`;

// Messages container
const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
  scrollbar-color: ${border} transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${border};
    border-radius: 4px;
  }
`;

// Individual message bubble
const MessageRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  animation: ${fadeIn} 0.25s ease;
  ${({ $isUser }) => $isUser && "flex-direction: row-reverse;"}
`;

const MsgAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isUser }) => ($isUser ? "rgba(64,224,208,0.15)" : cardBg)};
  border: 1px solid ${border};

  svg {
    font-size: 16px;
    color: ${({ $isUser }) => ($isUser ? primary : textTertiary)};
  }
`;

const BubbleWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  flex-direction: ${({ $isUser }) => ($isUser ? "row-reverse" : "row")};
`;

// const Bubble = styled.div`
//   // max-width: 100%;
//   padding: 10px 14px;
//   border-radius: ${({ $isUser }) =>
//     $isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px"};
//   font-size: 13.5px;
//   line-height: 1.55;
//   font-family: "Inter", sans-serif;
//   word-break: break-word;

//   background: ${({ $isUser }) => ($isUser ? gradient : cardBg)};
//   color: ${textPrimary};
//   border: ${({ $isUser }) => ($isUser ? "none" : `1px solid ${border}`)};
// `;

const Bubble = styled.div`
  position: relative; /* ADD THIS */
  padding: 1px 14px;
  // padding-right: 34px; /* SPACE FOR ICON */

  border-radius: ${({ $isUser }) =>
    $isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px"};

  font-size: 13.5px;
  line-height: 1.55;
  font-family: "Inter", sans-serif;
  word-break: break-word;

  background: ${({ $isUser }) => ($isUser ? gradient : cardBg)};
  color: ${textPrimary};
  border: ${({ $isUser }) => ($isUser ? "none" : `1px solid ${border}`)};
`;

const Timestamp = styled.span`
  font-size: 10px;
  color: ${textTertiary};
  display: block;
  margin-top: 4px;
  text-align: ${({ $isUser }) => ($isUser ? "right" : "left")};
`;

// Typing indicator
const TypingWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  animation: ${fadeIn} 0.25s ease;
`;

const TypingBubble = styled.div`
  background: ${cardBg};
  border: 1px solid ${border};
  border-radius: 16px 16px 16px 4px;
  padding: 12px 16px;
  display: flex;
  gap: 4px;
  align-items: center;
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${primary};
  animation: ${typing} 1.2s infinite;
  animation-delay: ${({ $delay }) => $delay || "0s"};
`;

// Input area
const InputArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid ${border};
  background: ${cardBg};
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${border};
  border-radius: 24px;
  padding: 10px 16px;
  font-size: 13.5px;
  color: ${textPrimary};
  font-family: "Inter", sans-serif;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${textTertiary};
  }

  &:focus {
    border-color: ${primary};
  }
`;

const SendBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    transform 0.2s,
    opacity 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  svg {
    font-size: 18px;
    color: #fff;
  }
`;

// Welcome banner shown when no messages yet
const WelcomeBanner = styled.div`
  text-align: center;
  padding: 24px 16px;
`;

const WelcomeIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;

  svg {
    font-size: 30px;
    color: #fff;
  }
`;

const WelcomeTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: ${textPrimary};
  font-family: "Inter", sans-serif;
`;

const WelcomeSub = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${textTertiary};
  font-family: "Inter", sans-serif;
  line-height: 1.5;
`;

const QuickReplies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
`;

const QuickBtn = styled.button`
  background: rgba(64, 224, 208, 0.08);
  border: 1px solid rgba(64, 224, 208, 0.3);
  border-radius: 20px;
  color: ${primary};
  font-size: 12px;
  font-family: "Inter", sans-serif;
  padding: 6px 14px;
  cursor: pointer;
  transition:
    background 0.2s,
    transform 0.15s;

  &:hover {
    background: rgba(64, 224, 208, 0.18);
    transform: scale(1.04);
  }
`;

const CopyBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 0px;

  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;

  opacity: 0;
  transition: 0.2s;

  ${Bubble}:hover & {
    opacity: 1;
  }
`;

const SC = {
  FAB,
  ChatWindow,
  Header,
  AvatarWrap,
  HeaderInfo,
  HeaderTitle,
  HeaderSub,
  OnlineDot,
  IconBtn,
  MessagesArea,
  MessageRow,
  MsgAvatar,
  Bubble,
  Timestamp,
  TypingWrap,
  TypingBubble,
  Dot,
  InputArea,
  Input,
  SendBtn,
  WelcomeBanner,
  WelcomeIcon,
  WelcomeTitle,
  WelcomeSub,
  QuickReplies,
  QuickBtn,
  BubbleWrap,
  CopyBtn,
};

export default SC;
